import { useContext } from "react";
import { useLocalization } from "@fluent/react";
import { Tooltip } from "@narmi/design_system";
import { catcat as cc } from "cerulean";
import Filters from "byzantine/src/filters";
import InstitutionSettingsContext from "../../../contexts/InstitutionSettingsContext";
import styles from "../../DualApproval.module.scss";
import type { TopCardProps } from "./types";

const PaymentScheduleDetails = ({
  dualApproval,
  isPendingDrawer = true,
}: TopCardProps) => {
  const { l10n } = useLocalization();
  const { ach_next_cutoff: achNextCutoff } = useContext(
    InstitutionSettingsContext,
  );

  const isACH = Boolean(dualApproval.achId);
  const isScheduledACH = Boolean(dualApproval.scheduledACHId);
  const isOneTimeScheduledACH =
    isScheduledACH && dualApproval.schedule?.recurrence === 1;

  if (isScheduledACH) {
    if (isOneTimeScheduledACH && dualApproval.schedule?.scheduledDate) {
      return (
        <>
          <h5 className={styles.drawerLabelHeading}>
            {l10n.getString("label-scheduled-date")}
          </h5>
          <span>
            {Filters.longMonthDayYear(dualApproval.schedule.scheduledDate)}
          </span>
        </>
      );
    }

    const recurrence = dualApproval.schedule?.recurrence;
    const showCount = recurrence && recurrence < 100;
    let endString = l10n.getString("approval-drawer-value-indefinite");

    if (dualApproval.schedule?.endDate) {
      endString = Filters.longMonthDayYear(dualApproval.schedule.endDate);
    } else if (showCount) {
      endString = l10n.getString("approval-drawer-value-ends-count", {
        count: recurrence,
      });
    }

    const totalAmount = showCount
      ? Filters.currency(
          (recurrence *
            Filters.dollarsToPennies(dualApproval.dollarAmount)) as Cents,
        )
      : "";

    return (
      <>
        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("approval-drawer-label-payment-schedule")}
        </h5>
        <div className="margin--bottom--m">
          {l10n.getString("approval-drawer-value-payment-schedule", {
            frequency: dualApproval.schedule?.frequency || "",
            startDate: dualApproval.schedule?.scheduledDate
              ? Filters.longMonthDayYear(dualApproval.schedule.scheduledDate)
              : "",
          })}
        </div>
        <h5 className={styles.drawerLabelHeading}>
          {l10n.getString("approval-drawer-label-schedule-ends")}
        </h5>
        <div className={cc({ "margin--bottom--m": Boolean(totalAmount) })}>
          {endString}
        </div>
        {showCount ? (
          <>
            <h5 className={styles.drawerLabelHeading}>
              {l10n.getString("approval-drawer-label-payment-count", {
                count: recurrence,
              })}
            </h5>
            <div>{totalAmount}</div>
          </>
        ) : null}
      </>
    );
  }

  return (
    <>
      <h5 className={styles.drawerLabelHeading}>
        {l10n.getString("approval-drawer-label-payment-schedule")}
      </h5>
      <span>
        {isPendingDrawer
          ? l10n.getString("approval-drawer-one-time-payment")
          : l10n.getString("history-drawer-one-time-payment")}
      </span>
      {isACH && isPendingDrawer && achNextCutoff ? (
        <Tooltip
          maxWidth="210px"
          text={l10n.getString("pending-approval-achcutoff-copy", {
            cutoff: new Date(achNextCutoff * 1000).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            }),
          })}
        >
          <span
            className={cc(
              "fontSize--m narmi-icon-alert-circle margin--left--xs",
            )}
          />
        </Tooltip>
      ) : null}
    </>
  );
};

export default PaymentScheduleDetails;
